import type { BulkThreadResult, Thread, ThreadStatus, UpdateThreadRequest } from '@central/api';
import { color } from '@central/ui';
import { Alert, Button, Checkbox, Menu } from '@mantine/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { keys, useApi } from '../api';
import { errorMessage } from '../lib/errors';
import { muted } from '../lib/labels';
import { useCanWrite, useMe } from '../lib/me';

/** The most threads one bulk change takes (POST /threads/bulk). */
const maxBulkThreads = 100;

const bulkStatuses: Array<{ value: ThreadStatus; label: string }> = [
  { value: 'open', label: 'Open' },
  { value: 'waiting_on_us', label: 'Waiting on us' },
  { value: 'waiting_on_customer', label: 'Waiting on customer' },
  { value: 'on_hold', label: 'On hold' },
  { value: 'closed', label: 'Resolved' },
];

const failureLabel: Record<string, string> = { not_found: 'no longer exists' };

/** A list row's tick box, beside (not inside) the row's link. */
export function RowCheck({
  t,
  checked,
  onChange,
}: {
  t: Thread;
  checked: boolean;
  onChange: (on: boolean) => void;
}) {
  return (
    <Checkbox
      aria-label={`Select ${t.ref}`}
      checked={checked}
      onChange={(e) => onChange(e.currentTarget.checked)}
      style={{ padding: '0 0 0 20px', display: 'flex', alignItems: 'center' }}
    />
  );
}

/**
 * Select-all for the loaded page and, with threads ticked, Resolve, Reopen,
 * Set status and Assign to… over all of them. Each change goes through
 * POST /threads/bulk; threads it could not change stay ticked and are named.
 */
export function BulkBar({
  items,
  selected,
  onSelect,
}: {
  items: Thread[];
  selected: ReadonlySet<string>;
  onSelect: (next: ReadonlySet<string>) => void;
}) {
  const api = useApi();
  const qc = useQueryClient();
  const assignees = useAssignees();
  const [failed, setFailed] = useState<BulkThreadResult[]>([]);
  const ids = items.map((t) => t.id).filter((id) => selected.has(id));
  const all = items.length > 0 && ids.length === items.length;
  const bulk = useMutation({
    mutationFn: (change: UpdateThreadRequest) => api.threads.bulk({ ids, ...change }),
    onSuccess: (res) => {
      const bad = res.results.filter((r) => !r.ok);
      setFailed(bad);
      onSelect(new Set(bad.map((r) => r.id)));
    },
    onSettled: () => void qc.invalidateQueries({ queryKey: keys.threads }),
  });
  const run = (change: UpdateThreadRequest) => {
    setFailed([]);
    bulk.mutate(change);
  };
  const tooMany = ids.length > maxBulkThreads;
  const busy = bulk.isPending;
  const refOf = (id: string) => items.find((t) => t.id === id)?.ref ?? id;

  return (
    <div
      role="group"
      aria-label="Bulk actions"
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 8,
        alignItems: 'center',
        padding: '10px 20px',
        borderBottom: `1px solid ${color.rowRule}`,
      }}
    >
      <Checkbox
        label={ids.length > 0 ? `${ids.length} selected` : 'Select all'}
        aria-label="Select all"
        checked={all}
        indeterminate={ids.length > 0 && !all}
        disabled={items.length === 0}
        onChange={() => onSelect(all ? new Set() : new Set(items.map((t) => t.id)))}
      />
      {ids.length > 0 && (
        <>
          <Button
            size="xs"
            color="dark"
            loading={busy && bulk.variables?.status === 'closed'}
            disabled={busy || tooMany}
            onClick={() => run({ status: 'closed' })}
          >
            Resolve
          </Button>
          <Button
            size="xs"
            variant="default"
            loading={busy && bulk.variables?.status === 'waiting_on_us'}
            disabled={busy || tooMany}
            onClick={() => run({ status: 'waiting_on_us' })}
          >
            Reopen
          </Button>
          <Menu position="bottom-start">
            <Menu.Target>
              <Button size="xs" variant="default" disabled={busy || tooMany}>
                Set status
              </Button>
            </Menu.Target>
            <Menu.Dropdown>
              {bulkStatuses.map((s) => (
                <Menu.Item key={s.value} onClick={() => run({ status: s.value })}>
                  {s.label}
                </Menu.Item>
              ))}
            </Menu.Dropdown>
          </Menu>
          <Menu position="bottom-start">
            <Menu.Target>
              <Button size="xs" variant="default" disabled={busy || tooMany}>
                Assign to…
              </Button>
            </Menu.Target>
            <Menu.Dropdown>
              {assignees.map((a) => (
                <Menu.Item key={a.value} onClick={() => run({ assigneeId: a.value })}>
                  {a.label}
                </Menu.Item>
              ))}
            </Menu.Dropdown>
          </Menu>
          <Button size="xs" variant="subtle" disabled={busy} onClick={() => onSelect(new Set())}>
            Clear
          </Button>
        </>
      )}
      {tooMany && (
        <span style={{ fontSize: 13, ...muted }}>
          Select at most {maxBulkThreads} conversations at a time.
        </span>
      )}
      {bulk.error && (
        <Alert color="red" w="100%">
          {errorMessage(bulk.error)}
        </Alert>
      )}
      {failed.length > 0 && (
        <Alert color="red" w="100%">
          {failed.length} not changed:{' '}
          {failed
            .map((r) => `${refOf(r.id)} (${failureLabel[r.error ?? ''] ?? r.error})`)
            .join(', ')}
        </Alert>
      )}
    </div>
  );
}

/**
 * Who a thread can be assigned to: the caller, every enabled staff user
 * (when the caller can list staff) and Unassigned ("").
 */
function useAssignees(): Array<{ value: string; label: string }> {
  const api = useApi();
  const me = useMe();
  const canListStaff = useCanWrite();
  const staff = useQuery({
    queryKey: keys.staff,
    queryFn: () => api.staff.list(),
    enabled: canListStaff,
    staleTime: 60_000,
  });
  const people = new Map<string, string>([[me.user.id, `${me.user.name} (me)`]]);
  for (const s of staff.data?.items ?? [])
    if (s.enabled && !people.has(s.id)) people.set(s.id, s.name);
  return [
    ...[...people].map(([value, label]) => ({ value, label })),
    { value: '', label: 'Unassigned' },
  ];
}
